import { motion } from "framer-motion";
import { ArrowUpRight, KeyRound, Building2, Home, Sofa, Shirt, Sparkles } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const TENANCY_IMG =
  "https://images.unsplash.com/photo-1580256081112-e49377338b7f?auto=format&fit=crop&w=1600&q=80";
const OFFICE_IMG =
  "https://images.pexels.com/photos/5511093/pexels-photo-5511093.jpeg?auto=compress&cs=tinysrgb&w=1200";

const ease = [0.16, 1, 0.3, 1];
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.75, delay, ease },
});

const ImageCard = ({ testid, img, icon: Icon, title, benefit, copy, className }) => (
  <motion.article
    {...reveal()}
    data-testid={testid}
    className={`group relative overflow-hidden rounded-3xl border border-slate-100 shadow-sm transition-transform duration-500 hover:-translate-y-1 hover:shadow-xl ${className}`}
  >
    <img
      src={img}
      alt={title}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/20 to-transparent" />
    <div className="relative flex h-full min-h-[320px] flex-col justify-end p-8 lg:min-h-[400px] lg:p-10">
      <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-teal-200 backdrop-blur-sm">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="font-serif text-3xl font-semibold tracking-tight text-white">{title}</h3>
      <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-teal-300">{benefit}</p>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-200">{copy}</p>
    </div>
  </motion.article>
);

const TextCard = ({ testid, icon: Icon, title, benefit, copy, dark, className }) => (
  <motion.article
    {...reveal(0.08)}
    data-testid={testid}
    className={`group flex h-full flex-col justify-between rounded-3xl border p-8 shadow-sm transition-transform duration-500 hover:-translate-y-1 hover:shadow-xl lg:p-10 ${
      dark ? "border-slate-800 bg-slate-900" : "border-slate-100 bg-white"
    } ${className}`}
  >
    <div>
      <span
        className={`mb-6 flex h-11 w-11 items-center justify-center rounded-2xl ${
          dark ? "bg-teal-500/15 text-teal-300" : "bg-teal-50 text-teal-700"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <h3 className={`font-serif text-2xl font-semibold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>
        {title}
      </h3>
      <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-teal-600">{benefit}</p>
      <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-500"}`}>{copy}</p>
    </div>
    <button
      data-testid={`${testid}-quote-link`}
      onClick={() => scrollToSection("#contact")}
      className={`mt-6 flex w-fit items-center gap-1.5 text-sm font-bold transition-colors ${
        dark ? "text-teal-300 hover:text-teal-200" : "text-teal-700 hover:text-teal-600"
      }`}
    >
      Get a quote
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </button>
  </motion.article>
);

const SmallCard = ({ testid, icon: Icon, title, copy, className }) => (
  <motion.article
    {...reveal(0.1)}
    data-testid={testid}
    className={`flex h-full flex-col gap-4 rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-transform duration-500 hover:-translate-y-1 hover:shadow-xl ${className}`}
  >
    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <h3 className="font-serif text-xl font-semibold tracking-tight text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">{copy}</p>
    </div>
  </motion.article>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="bg-slate-50 py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-12">
      <motion.div {...reveal()} className="mb-14 max-w-2xl lg:mb-20">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-teal-700">Our Services</p>
        <h2 className="font-serif text-4xl font-medium tracking-tight text-slate-900 sm:text-5xl">
          Every space, <span className="italic text-teal-700">immaculate.</span>
        </h2>
        <p className="mt-5 text-sm leading-relaxed text-slate-500 sm:text-base">
          From weekly home visits to full end of tenancy deep cleans, every Hyek service is built
          around one promise: a healthier, happier space for the people who live and work in it.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
        <ImageCard
          testid="service-card-tenancy"
          img={TENANCY_IMG}
          icon={KeyRound}
          title="End of Tenancy Cleaning"
          benefit="Get your full deposit back"
          copy="A checklist-perfect deep clean that satisfies the strictest letting agents and landlords — so you hand back the keys with total confidence."
          className="md:col-span-8"
        />
        <ImageCard
          testid="service-card-office"
          img={OFFICE_IMG}
          icon={Building2}
          title="Office Cleaning"
          benefit="A workplace that works"
          copy="Spotless desks, fresh air, healthier teams. Flexible daily, weekly or out-of-hours schedules that never interrupt your business."
          className="md:col-span-4"
        />
        <TextCard
          testid="service-card-domestic"
          icon={Home}
          title="Domestic & Residential"
          benefit="Come home to calm"
          copy="Regular visits from the same trusted cleaner, every room left fresh — so your evenings and weekends stay yours."
          className="md:col-span-6"
        />
        <TextCard
          testid="service-card-carpet"
          icon={Sofa}
          title="Carpet & Upholstery"
          benefit="Deep-clean revival"
          copy="Hot-water extraction lifts ground-in stains, allergens and odours. Your carpets and sofas look, feel and smell new again."
          dark
          className="md:col-span-6"
        />
        <SmallCard
          testid="service-card-laundry"
          icon={Shirt}
          title="Laundry & Ironing"
          copy="Collected, laundered, crisply pressed and returned — one less chore on your list."
          className="md:col-span-4"
        />
        <SmallCard
          testid="service-card-vacuuming"
          icon={Sparkles}
          title="Vacuuming & Dusting"
          copy="A detailed, dust-free finish in every corner — from skirting boards to ceiling lines."
          className="md:col-span-4"
        />
        <motion.div
          {...reveal(0.12)}
          data-testid="service-card-cta"
          className="flex h-full flex-col justify-between rounded-3xl bg-teal-700 p-8 shadow-sm transition-transform duration-500 hover:-translate-y-1 hover:shadow-xl md:col-span-4"
        >
          <h3 className="font-serif text-2xl font-semibold tracking-tight text-white">
            Need something <span className="italic">bespoke?</span>
          </h3>
          <div>
            <p className="mt-3 text-sm leading-relaxed text-teal-100">
              One-off deep cleans, after-builders cleans, seasonal refreshes — just ask.
            </p>
            <button
              data-testid="service-cta-quote-button"
              onClick={() => scrollToSection("#contact")}
              className="mt-6 rounded-full bg-white px-6 py-3 text-sm font-bold text-teal-800 transition-transform duration-300 hover:scale-105"
            >
              Ask for a Quote
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
